import type { QueryClient } from '@tanstack/react-query';
import type { SessionSnapshot } from '../api/contract';
import { queryKeys } from '../api/query-keys';

/** Which organisation this browser last entered. Not who entered it. */
export const TENANT_STORAGE_KEY = 'ribox-crm.tenant';

/** The sliver of `Storage` this needs, so a test can hand over a double. */
export interface TenantChoiceStorage {
  removeItem(key: string): void;
}

/**
 * `localStorage` when there is one, and nothing when there is not: the test
 * runner has no DOM, and private-browsing modes throw on the mere access.
 */
export function browserTenantStorage(): TenantChoiceStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Forgets who was signed in — every session snapshot and the organisation this
 * browser had chosen.
 *
 * Sign-out and an expired session are the same cleanup under two triggers, so
 * they share one implementation. It drops the *whole* `['session', …]` subtree
 * rather than the current key: the login screen reads `session(null)`, and a
 * snapshot left under any key is one refetch away from being served to whoever
 * sits down next. In-flight session queries are cancelled first, or the reply
 * to a request made while still authenticated would land in the cache we just
 * emptied.
 *
 * Then it *states* the outcome instead of leaving a hole. Removing alone is not
 * enough: the provider's observer re-points at `session(null)` on the very next
 * render and fills a hole by asking again — while the cookie is still alive,
 * because `signOut` does not await the server, and permanently alive if that
 * round trip never succeeds. The answer to that question describes the person
 * who just left and it lands under the anonymous key, which is exactly the
 * write this whole module exists to prevent. Writing `null` answers it up
 * front, and the entry is fresh for `STALE_TIME.reference`, so nothing is
 * scheduled that could race the sweep.
 */
export function forgetSession(
  queryClient: QueryClient,
  storage: TenantChoiceStorage | null = browserTenantStorage(),
): void {
  void queryClient.cancelQueries({ queryKey: queryKeys.sessions() });
  queryClient.removeQueries({ queryKey: queryKeys.sessions() });
  queryClient.setQueryData<SessionSnapshot | null>(queryKeys.session(null), null);
  storage?.removeItem(TENANT_STORAGE_KEY);
}

/**
 * Vuelve a preguntar quién está firmado —bajo la clave que el proveedor esté
 * mirando, sea cual sea— y espera la respuesta.
 *
 * PARA QUÉ. Quien acaba de autenticarse y tiene más de una organización todavía
 * no eligió ninguna, así que la consulta que el proveedor mira sigue teniendo la
 * respuesta de antes de la contraseña: un `null` de `forgetSession`, o el `null`
 * que devolvió el 401 de `/api/auth/me` cuando se pintó el login. Ese `null` era
 * cierto hace un segundo y ya no lo es: con él en la caché, el selector de
 * organización ve una sesión anónima y rebota al login que la persona acaba de
 * pasar.
 *
 * NO SE PREGUNTA POR `session(null)`, Y ÉSE ERA EL DEFECTO. Hasta acá esto
 * refrescaba la clave anónima y sólo ésa, dando por sentado que el proveedor la
 * estaba mirando. No siempre: `tenantId` sale de `localStorage`, sobrevive a una
 * cookie vencida —el 401 de `/api/auth/me` viaja con `anonymousAllowed`, así que
 * NO dispara la limpieza de sesión— y el proveedor queda mirando
 * `session('la-de-la-visita-anterior')`. Refrescar la anónima entonces es
 * refrescar una consulta que no mira nadie: la que gobierna la pantalla se queda
 * con su `null`, el selector rebota al login, y sólo recargar entero —que
 * reconstruye la caché— deja entrar. Se invalida el SUBÁRBOL `['session', …]`,
 * que alcanza a la consulta viva se llame como se llame.
 *
 * PREGUNTA, NO ESCRIBE, y ésa es toda la diferencia con `rememberSession`. No
 * archiva ninguna instantánea bajo una clave ajena —esa regla sigue intacta y su
 * prueba también—: `invalidateQueries` vuelve a pedir las consultas ACTIVAS y a
 * las inactivas sólo las marca viejas, así que lo que queda anotado en cada
 * clave es lo que el servidor contestó para esa clave, con la cookie viva.
 *
 * SE ESPERA A PROPÓSITO. Mientras el viaje está en el aire la caché sigue
 * diciendo «no hay nadie», así que navegar antes de que aterrice llevaría al
 * selector justo al estado que esto viene a corregir.
 */
export async function refreshSession(queryClient: QueryClient): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: queryKeys.sessions() });
}

/**
 * Files a sign-in snapshot under the organisation it describes, so the provider
 * finds the answer already there instead of asking for it again.
 *
 * Two rules, and both are the leak stated as a positive: it is never written
 * under the anonymous key, which is the login screen's; and a snapshot the
 * server resolved for one organisation is not filed under another, because the
 * person's row, role and even name can differ between them. A snapshot that
 * does not describe its key is dropped — the provider then asks, which is
 * cheap and true.
 *
 * Returns whether it was cached, which is what makes the rule testable.
 */
export function rememberSession(
  queryClient: QueryClient,
  tenantId: string,
  snapshot: SessionSnapshot,
): boolean {
  if (!tenantId || snapshot.user.tenantId !== tenantId) return false;
  queryClient.setQueryData(queryKeys.session(tenantId), snapshot);
  return true;
}
